import { notNullish } from 'lib/notNullish';

import { PortCall as ServerPortCall } from 'server/models/PortCall';
import { BerthReservation } from './BerthReservation';
import { BerthReservationStatus } from 'server/enums/BerthReservation';
import { ItineraryPoint } from 'data/models/ItineraryPoint';
import { ImmutableNullableMeasurement, NullableMeasurement, NullableMeasurementLike } from 'data/models/Measurement';
import { PCUser } from 'data/models/PCUser';
import { ScheduleDateEntity } from 'data/models/ScheduleDateEntity';
import { Vessel } from 'data/models/Vessel';

export { PortCallOptions, PortCallData } from 'server/models/PortCall';

export class PortCall extends ServerPortCall {
    Reservations() {
        const pts = this.Itinerary(),
            reses = [] as BerthReservation[];
            
        for (let pt of pts) {
            let res = pt.BerthReservation();
            if (res) reses.push(res);
        }

        reses.sort((a, b) => a.Itinerary() && b.Itinerary() ? ItineraryPoint.EstimatedOrder(a.Itinerary()!, b.Itinerary()!) : 0);

        return reses;
    }

    ActiveReservations() {
        return this.Reservations().filter(r => r.Status() !== BerthReservationStatus.Cancelled);
    }

    Booked() {
        var reses = this.Reservations();
        return reses.every(r => r.Status() === BerthReservationStatus.Booked || r.Status() === BerthReservationStatus.Cancelled)
            && reses.some(r => r.Status() !== BerthReservationStatus.Cancelled);
    }

    Cancelled() {
        var reses = this.Reservations();
        return reses.length > 0 && reses.every(res => res.CancelledDate() !== null);
    }

    DisplayVesselIdentifier() {
        return VesselIdentifier(() => this.Vessel(), () => this.Agent(), this.LengthOnlyLength);
    }

    get VesselLength() {
        return new ImmutableNullableMeasurement({
            Num: () => this.VesselId() === null ?
                this.LengthOnlyLength.Num() :
                this.Vessel()?.Length.Num() ?? null,
            UOM: () => this.VesselId() === null ?
                this.LengthOnlyLength.UOM() :
                this.Vessel()?.Length.UOM() ?? null
        });
    }

    MovementSeries() {
        const itn = this.Itinerary(),
            // HACK: currently, you can't re-order shifts, so creation order = shift order, 
            // allowing us to order on Id
            // if we don't have an Id yet, we assume that it's a new point and use its position in
            // this.Itinerary()
            // finally, we keep the port arrival/departure at the ends
            order = (pt : ItineraryPoint) => 
                pt.IsPortArrival() && Number.MIN_SAFE_INTEGER ||
                pt.IsPortDeparture() && Number.MAX_SAFE_INTEGER ||
                pt.Id() ||
                Number.MAX_SAFE_INTEGER - itn.length + itn.indexOf(pt);
        return itn.filter(pt => pt.IsMovementPoint())
            .sort((a, b) => order(a) - order(b));
    }

    MovementSchedule() {
        const out : (ScheduleDateEntity | null)[] = [];
        for (let pt of this.MovementSeries()) {
            out.push(pt.ArrivalEntity());
            out.push(pt.DepartureEntity());
        }
        return out;
    }

    MilestoneDates() {
        return this
            .MovementSchedule()
            .filter(notNullish)
            .filter(d => d.MilestoneTypeId() !== null);
    }
}

export function VesselIdentifier(vessel : () => Vessel | null, agent : () => PCUser | null, lengthOnlyLength : NullableMeasurementLike) {
    if (vessel()) return vessel()!.Name();
    const org = agent() && agent()!.Organization();

    return (org ? org.Name() + " - " : "") + new NullableMeasurement({
        Num: lengthOnlyLength.Num(),
        UOM: lengthOnlyLength.UOM()
    }).toString({ units: true, places: 1});
}


// WEBPACK FOOTER //
// ./data/models/PortCall.ts